/* eslint-disable no-param-reassign */
import logo from '../../../../PageBuilder/interface/images/logo.png';
import { getClimateZone, getMinimumRSI } from '../../helpers';

/** ========== Helpers ========== */
const isNum = v => typeof v === 'number' && Number.isFinite(v);
const toR = rsi => (isNum(rsi) ? rsi * 5.678 : 0);
const fmtRSI = v => (isNum(v) ? v.toFixed(2) : '00.00');
const fmtR = v => (isNum(v) ? toR(v).toFixed(2) : '00.00');
const fmtPct = v => (isNum(v) ? `${v.toFixed(2)}%` : '00.00%');

// Pink brand color used for zero/values to match UI
const PINK = [212, 15, 125];
const GREY_BAND = [230, 230, 230];
const TABLE_FILL = [255, 255, 255];
const GRID = [220, 220, 220];
const STRIPE_FILL = [248, 248, 248];
const BLACK = [0, 0, 0];
const TITLE_MAX_LEN = 70; // if title exceeds this, split into two lines

function titleText(doc, text, x, y) {
  if (!text) return; // guard against undefined
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(0);
  doc.setFontSize(18);
  doc.text(String(text), x, y);
}

function paragraph(doc, text, x, y, maxWidth) {
  if (!text) return; // guard against undefined
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(0);
  doc.setFontSize(10);
  doc.text(String(text), x, y, { maxWidth });
}

function sectionKicker(doc, text, x, y) {
  if (!text) return; // guard against undefined
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(0);
  doc.setFontSize(14);
  if (text.length > TITLE_MAX_LEN) {
    // If the text is very long, split into two lines
    const split = doc.splitTextToSize(String(text), 400);
    doc.text(split, x, y);
  } else {
    doc.text(String(text), x, y);
  }
}

/** Build overview rows from ResultsBlock row config */
function buildOverviewRows(rowsConfig, results) {
  return (rowsConfig || [])
    .filter(r => (typeof r.show === 'function' ? !!r.show(results) : true))
    .map(r => {
      // eslint-disable-next-line no-nested-ternary
      const value = typeof r.rsi === 'function' ? r.rsi(results) : typeof r.rsi === 'string' ? results?.[r.rsi] : null;

      // If the row provides a text resolver, render its text and leave R empty
      if (typeof r.text === 'function') return [r.label, String(r.text(results) ?? ''), ''];

      // Explicit formatting options
      if (r.format === 'percent') return [r.label, fmtPct(value ?? 0), ''];
      if (r.format === 'textOnly') return [r.label, '', ''];

      // If it's clearly non-numeric but intended to be shown (e.g., "Yes"/"No"),
      // place the string in the RSI column and keep R blank.
      if (!isNum(value) && r.noR) return [r.label, String(value ?? ''), ''];

      // Default numeric formatting
      return [r.label, fmtRSI(value), r.noR ? '' : fmtR(value)];
    });
}

/** Build continuous / non-continuous layer rows (bands are BODY rows) */
function buildLayerRows(results, t) {
  const rows = [];
  const continuousResults = results?.layers?.continuous || [];
  const nonContinuousResults = results?.layers?.nonContinuous || [];

  const makeFirstCell = r => {
    if (r && typeof r.label === 'object' && r.label !== null && 'content' in r.label) return r.label;
    if (r?.labelBold && typeof r.label === 'string') {
      return { content: r.label, styles: { fontStyle: 'bold' } };
    }
    if (r?.labelSafe) return { content: r.labelSafe };
    return r?.label ?? '';
  };

  rows.push([
    {
      content: t('thermal_calculator.effective_thermal_resistance'),
      colSpan: 3,
      styles: { halign: 'center', fillColor: TABLE_FILL, fontStyle: 'bold' },
    },
  ]);

  if (continuousResults.length) {
    rows.push([
      {
        content: t('thermal_calculator.continuous_layers'),
        styles: { halign: 'left', fontStyle: 'bold', fillColor: GREY_BAND, textColor: BLACK },
      },
      {
        content: 'RSI',
        styles: { halign: 'center', fontStyle: 'bold', fillColor: GREY_BAND, textColor: BLACK },
      },
      {
        content: 'R',
        styles: { halign: 'center', fontStyle: 'bold', fillColor: GREY_BAND, textColor: BLACK },
      },
    ]);

    continuousResults.forEach(r => {
      const highlight = !!r.highlight;
      const rsiCell = highlight ? { content: fmtRSI(r.rsi), _highlight: true } : fmtRSI(r.rsi);
      const rCell = highlight ? { content: fmtR(r.rsi), _highlight: true } : fmtR(r.rsi);
      rows.push([makeFirstCell(r), rsiCell, rCell]);
    });
  }

  if (nonContinuousResults.length) {
    rows.push([
      {
        content: t('thermal_calculator.non_continuous_layers'),
        colSpan: 3,
        styles: { halign: 'left', fontStyle: 'bold', fillColor: GREY_BAND, textColor: BLACK },
      },
    ]);

    nonContinuousResults.forEach(r => {
      const highlight = !!r.highlight;
      const rsiCell = highlight ? { content: fmtRSI(r.rsi), _highlight: true } : fmtRSI(r.rsi);
      const rCell = highlight ? { content: fmtR(r.rsi), _highlight: true } : fmtR(r.rsi);
      rows.push([makeFirstCell(r), rsiCell, rCell]);
    });
  }

  return rows;
}

/** ========== Main ========== */
// async because jspdf loads on demand - it's too heavy to bundle into every
// pack that carries the Builder library
export default async function generateResultsPdf({
  t,
  // meta
  calcId,
  province,
  region,
  hdd,
  // headings
  assemblyHeading,
  framingLabel,
  coverTitle = `${t('thermal_calculator.effective_thermal_resistance')} - ${t('thermal_calculator.results.title')}`,
  // table meta
  tableTitle = t('thermal_calculator.entire_assembly'),
  tableSubtitle = t('thermal_calculator.effective_thermal_resistance'),
  rowsConfig,
  results,
  footnotes = [
    `1. ${t('thermal_calculator.footnotes.default_1')}`,
    // eslint-disable-next-line max-len
    `2. ${t('thermal_calculator.footnotes.default_2')}`,
    `3. ${t('thermal_calculator.footnotes.default_3')}`,
  ],
  // Optional: pass existing doc to add pages, or create new one
  existingDoc = null,
  skipSave = false,
}) {
  const [{ default: jsPDF }, { default: autoTable }] = await Promise.all([import('jspdf'), import('jspdf-autotable')]);
  // eslint-disable-next-line new-cap
  const doc = existingDoc || new jsPDF({ unit: 'pt', format: 'a4' });

  // If using existing doc, add a new page
  if (existingDoc) {
    doc.addPage();
  }

  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();

  const LEFT = 40;
  const TOP = 40;

  /** ---------- Header (logo + title + intro) ---------- */
  let logoH = 40;
  const LOGO_W = 40;

  try {
    const props = doc.getImageProperties(logo);
    const ratio = props.height / props.width || 1;
    logoH = LOGO_W * ratio;
    doc.addImage(logo, 'PNG', LEFT, TOP, LOGO_W, logoH);
  } catch {
    // Fallback if getImageProperties fails in some environments
    doc.addImage(logo, 'PNG', LEFT, TOP, LOGO_W, logoH);
  }

  const titleY = TOP + logoH + 25;
  titleText(doc, coverTitle, LEFT, titleY);

  const introY = titleY + 24;
  const rightMargin = 40;
  const introMaxW = pageW - LEFT - rightMargin;
  paragraph(
    doc,
    // eslint-disable-next-line max-len
    t('thermal_calculator.pdf_disclaimer'),
    LEFT,
    introY,
    introMaxW
  );

  /** ---------- Province / Region / HDD card (left) ---------- */
  autoTable(doc, {
    startY: introY + 48,
    margin: { left: LEFT },
    tableWidth: 280,
    head: [],
    body: [
      [t('thermal_calculator.results.province'), province || '—'],
      [t('thermal_calculator.results.region'), region || '—'],
      [t('thermal_calculator.results.heating_degree_days'), isNum(hdd) ? hdd : '—'],
    ],
    styles: { font: 'helvetica', fontSize: 10, cellPadding: 8, halign: 'left', valign: 'middle' },
    columnStyles: { 0: { fontStyle: 'bold', cellWidth: 160 }, 1: { cellWidth: 150 } },
    theme: 'grid',
    didParseCell: data => {
      if (data.section === 'body' && data.row.index % 2 === 0) {
        data.cell.styles.fillColor = STRIPE_FILL;
      } else if (data.section === 'body') {
        data.cell.styles.fillColor = TABLE_FILL;
      }
    },
  });

  // Compliance header
  const climateZone = getClimateZone(hdd);
  const minimumRSI = getMinimumRSI({ climateZone, calcId });
  const effectiveAssemblyRSI = results?.effectiveAssemblyRSI?.toFixed(2);
  const isCompliant = effectiveAssemblyRSI > minimumRSI;

  autoTable(doc, {
    startY: introY + 150,
    margin: { left: LEFT },
    tableWidth: 280,
    head: [],
    body: [
      [t('thermal_calculator.header.climate_zone'), climateZone || '—'],
      [t('thermal_calculator.header.minimum_rsi'), minimumRSI || '—'],
      [t('thermal_calculator.header.effective_r_value'), effectiveAssemblyRSI || '—'],
      [
        t('thermal_calculator.header.compliant'),
        isCompliant ? t('thermal_calculator.answer_yes') : t('thermal_calculator.answer_no'),
      ],
    ],
    styles: { font: 'helvetica', fontSize: 10, cellPadding: 8, halign: 'left', valign: 'middle' },
    columnStyles: { 0: { fontStyle: 'bold', cellWidth: 160 }, 1: { cellWidth: 150 } },
    theme: 'grid',
    didParseCell: data => {
      if (data.section === 'body' && data.row.index % 2 === 0) {
        // eslint-disable-next-line no-param-reassign
        data.cell.styles.fillColor = STRIPE_FILL;
      } else if (data.section === 'body') {
        // eslint-disable-next-line no-param-reassign
        data.cell.styles.fillColor = TABLE_FILL;
      }
    },
  });

  let y = (doc.lastAutoTable?.finalY || introY + 14) + 40;

  /** ---------- Section Kicker (e.g., Roofs/cathedral ceilings) ---------- */
  const title = assemblyHeading || framingLabel || 'Assembly';
  sectionKicker(doc, title, LEFT, y);
  y += title.length > TITLE_MAX_LEN ? 30 : 18;

  /** ---------- Overview Table (pink values) ---------- */
  const bodyOverview = buildOverviewRows(rowsConfig, results);

  autoTable(doc, {
    startY: y,
    head: [[{ content: `${tableTitle} (${tableSubtitle})`, styles: { halign: 'left' } }, 'RSI', 'R']],
    body: bodyOverview,
    styles: {
      font: 'helvetica',
      fontSize: 11,
      cellPadding: 6,
      lineColor: GRID,
      lineWidth: 0.6,
      fillColor: TABLE_FILL,
    },
    headStyles: { textColor: 30, halign: 'left' },
    columnStyles: {
      0: { halign: 'left' },
      1: { halign: 'left', cellWidth: 80, textColor: PINK },
      2: { halign: 'left', cellWidth: 80, textColor: PINK },
    },
    margin: { left: LEFT, right: LEFT },
    theme: 'grid',
    didParseCell: data => {
      // alternate row background color
      if (data.section === 'body' && data.row.index % 2 === 0) {
        // even-index rows (first, third…) = grey band
        data.cell.styles.fillColor = STRIPE_FILL;
      } else if (data.section === 'body') {
        // odd rows transparent (white)
        data.cell.styles.fillColor = TABLE_FILL;
      }

      // first column dynamic width
      if (data.column.index === 0) {
        const total = pageW - 2 * LEFT;
        data.cell.styles.cellWidth = total - 140 - 120 - 20;
      }

      // ensure empty numeric cells still show 0.0 in pink
      if (data.section === 'body' && (data.column.index === 1 || data.column.index === 2)) {
        if (!data.cell.raw) data.cell.text = ['0.0'];
        data.cell.styles.textColor = PINK;
      }
    },
  });

  /** ---------- Layers Table (bands are BODY rows; only headers repeat) ---------- */
  /** ---------- Layers Table (bands are BODY rows; only headers repeat) ---------- */
  const hasLayers = Array.isArray(results?.layers?.continuous) || Array.isArray(results?.layers?.nonContinuous);
  if (hasLayers) {
    const startY = (doc.lastAutoTable?.finalY || y + 14) + 18;
    const layerRows = buildLayerRows(results, t);

    autoTable(doc, {
      startY,
      body: layerRows,
      styles: {
        font: 'helvetica',
        fontSize: 11,
        cellPadding: 6,
        lineColor: GRID,
        lineWidth: 0.6,
        fillColor: TABLE_FILL,
      },
      columnStyles: {
        0: { halign: 'left' },
        1: { halign: 'center', cellWidth: 140, textColor: BLACK },
        2: { halign: 'center', cellWidth: 120, textColor: BLACK },
      },
      margin: { left: LEFT, right: LEFT },
      theme: 'grid',
      showHead: 'everyPage',
      didParseCell: data => {
        const isBody = data.section === 'body';

        // Alternate striping
        if (isBody && data.row.index > 1) {
          if (data.row.index % 2 === 0) {
            data.cell.styles.fillColor = STRIPE_FILL;
          } else {
            data.cell.styles.fillColor = TABLE_FILL;
          }

          // Dynamic first-column width (skip 3-col band rows)
          const isBandRow = data.cell?.raw && typeof data.cell.raw === 'object' && data.cell.raw.colSpan === 3;
          if (data.column.index === 0 && !isBandRow) {
            const total = pageW - 2 * LEFT;
            data.cell.styles.cellWidth = total - 140 - 120;
          }
        }

        // numeric columns: default BLACK, PINK only when the cell has _highlight
        if (isBody && (data.column.index === 1 || data.column.index === 2)) {
          if (!data.cell.raw) data.cell.text = ['00.00'];
          const isHighlighted =
            // eslint-disable-next-line no-underscore-dangle
            data.cell?.raw && typeof data.cell.raw === 'object' && data.cell.raw._highlight === true;
          data.cell.styles.textColor = isHighlighted ? PINK : BLACK;
        }
      },
    });
  }

  /** ---------- Footnotes ---------- */
  let footY = Math.min(pageH - 60, (doc.lastAutoTable?.finalY || y + 14) + 18);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(60);
  doc.setFontSize(9);

  (footnotes || []).forEach(item => {
    if (!item) return;
    doc.text(String(item), LEFT, footY);
    footY += item.length > 127 ? 24 : 14;
  });

  /** ---------- Page numbers (skip if we're building a multi-assembly doc) ---------- */
  if (!existingDoc) {
    const pageCount = doc.internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i += 1) {
      doc.setPage(i);
      doc.setFontSize(9);
      doc.setTextColor(120);
      doc.text(t('thermal_calculator.pdf.page_of', { page: i, total: pageCount }), pageW - 80, pageH - 24);
    }
  }

  /** ---------- Save or return ---------- */
  if (!skipSave && !existingDoc) {
    const headingForName = assemblyHeading || framingLabel || 'Assembly';
    const fname = `Thermal_Results_${String(headingForName).replace(/\s+/g, '_')}_${province || 'NA'}_${
      region || 'NA'
    }.pdf`;
    doc.save(fname);
  }

  return doc;
}
/* eslint-enable no-param-reassign */
